import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Building2, User, Crown, Shield,
  Plus, Trash2, RefreshCw, LogOut, ChevronRight,
} from "lucide-react";
import api from "../../../../../const/axios";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Assignment {
  isOrganizer: boolean;
  isIndividualOrganizer: boolean;
  organizationId: number | null;
  organizationName: string | null;
  organizationRole: string | null;
}

interface OrgDetails {
  id: number;
  name: string;
  shortName: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  information: string | null;
  facebook: string | null;
  instagram: string | null;
  whatsapp: string | null;
  isApproved: boolean;
  myRole: string;
  members: Member[];
}

interface Member {
  userId: number;
  name: string;
  surname: string;
  role: string;
  assignedAt: string;
}

type View = "loading" | "no-status" | "individual" | "moderator" | "owner";
type OwnerTab = "info" | "members";

interface OrganizerManagementDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onStatusChange: () => void;
  currentUserId: number;
}

const emptyOrgForm = {
  name: "", shortName: "", city: "", phone: "", email: "",
  information: "", facebook: "", instagram: "", whatsapp: "",
};

// ─── Dialog ──────────────────────────────────────────────────────────────────

const OrganizerManagementDialog: React.FC<OrganizerManagementDialogProps> = ({
  open, onOpenChange, onStatusChange, currentUserId,
}) => {
  const [view, setView] = useState<View>("loading");
  const [ownerTab, setOwnerTab] = useState<OwnerTab>("info");
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [org, setOrg] = useState<OrgDetails | null>(null);

  // Form state
  const [orgForm, setOrgForm] = useState(emptyOrgForm);
  const [orgFormErrors, setOrgFormErrors] = useState<Record<string, string>>({});

  // Member management
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState<any[]>([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedNewMember, setSelectedNewMember] = useState<{ id: number; label: string } | null>(null);
  const [memberDropdown, setMemberDropdown] = useState(false);

  // Action state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmResign, setConfirmResign] = useState(false);
  const [confirmLeaveOrg, setConfirmLeaveOrg] = useState(false);

  // ── Data loading ─────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setView("loading");
    setError(null);
    setConfirmResign(false);
    setConfirmLeaveOrg(false);
    try {
      const res = await api.get("users/organizer/me");
      const a: Assignment = {
        isOrganizer: !!res.data?.isOrganizer,
        isIndividualOrganizer: !!res.data?.isIndividualOrganizer,
        organizationId: res.data?.organizationId ?? null,
        organizationName: res.data?.organizationName ?? null,
        organizationRole: res.data?.organizationRole ?? null,
      };
      setAssignment(a);

      if (!a.isOrganizer) {
        setView("no-status");
        return;
      }

      if (a.organizationId) {
        const orgRes = await api.get("organizations/my");
        const orgData: OrgDetails = orgRes.data;
        setOrg(orgData);
        setOrgForm({
          name: orgData.name ?? "",
          shortName: orgData.shortName ?? "",
          city: orgData.city ?? "",
          phone: orgData.phone ?? "",
          email: orgData.email ?? "",
          information: orgData.information ?? "",
          facebook: orgData.facebook ?? "",
          instagram: orgData.instagram ?? "",
          whatsapp: orgData.whatsapp ?? "",
        });
        setView(orgData.myRole === "owner" ? "owner" : "moderator");
      } else {
        setView("individual");
      }
    } catch {
      setView("no-status");
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const registerIndividual = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("users/organizer/register");
      onStatusChange();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const resignFromOrganizer = async () => {
    setBusy(true); setError(null);
    try {
      await api.delete("users/organizer/me");
      onStatusChange();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); setConfirmResign(false); }
  };

  const validateOrgForm = () => {
    const errs: Record<string, string> = {};
    if (!orgForm.name.trim()) errs.name = "Ad tələb olunur";
    if (!orgForm.city.trim()) errs.city = "Şəhər tələb olunur";
    setOrgFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const saveOrgDetails = async () => {
    if (!validateOrgForm() || !org) return;
    setBusy(true); setError(null);
    try {
      await api.patch(`organizations/${org.id}/details`, {
        name: orgForm.name.trim(),
        shortName: orgForm.shortName.trim() || orgForm.name.trim(),
        city: orgForm.city.trim(),
        phone: orgForm.phone || null,
        email: orgForm.email || null,
        information: orgForm.information || null,
        facebook: orgForm.facebook || null,
        instagram: orgForm.instagram || null,
        whatsapp: orgForm.whatsapp || null,
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const leaveOrg = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("users/organizer/me/leave-org");
      onStatusChange();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); setConfirmLeaveOrg(false); }
  };

  const removeMember = async (userId: number) => {
    if (!org) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`organizations/${org.id}/members/${userId}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const transferOwnership = async (userId: number) => {
    if (!org) return;
    setBusy(true); setError(null);
    try {
      await api.put(`organizations/${org.id}/transfer-owner/${userId}`);
      onStatusChange();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const addMember = async () => {
    if (!org || !selectedNewMember) return;
    setBusy(true); setError(null);
    try {
      await api.post(`organizations/${org.id}/members`, {
        userId: selectedNewMember.id,
        role: "moderator",
      });
      setSelectedNewMember(null);
      setMemberSearch("");
      setMemberSearchResults([]);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Xəta baş verdi.");
    } finally { setBusy(false); }
  };

  const searchMembers = async (val: string) => {
    setMemberSearch(val);
    setSelectedNewMember(null);
    if (!val.trim()) { setMemberSearchResults([]); setMemberDropdown(false); return; }
    setMemberSearchLoading(true);
    try {
      const res = await api.get(`users?search=${encodeURIComponent(val)}&perPage=8`);
      const list = (res.data?.users ?? res.data?.Users ?? []).map((u: any) => ({
        id: u.id ?? u.ID,
        label: `${u.name ?? u.Name} ${u.surname ?? u.Surname}`.trim(),
      }));
      setMemberSearchResults(list);
      setMemberDropdown(true);
    } catch {
      setMemberSearchResults([]);
    } finally { setMemberSearchLoading(false); }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderField = (
    label: string,
    key: keyof typeof orgForm,
    opts?: { placeholder?: string; textarea?: boolean; required?: boolean }
  ) => (
    <div>
      <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">
        {label}{opts?.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {opts?.textarea ? (
        <textarea
          value={orgForm[key]}
          onChange={e => setOrgForm(f => ({ ...f, [key]: e.target.value }))}
          placeholder={opts.placeholder}
          rows={3}
          className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        />
      ) : (
        <Input
          value={orgForm[key]}
          onChange={e => setOrgForm(f => ({ ...f, [key]: e.target.value }))}
          placeholder={opts?.placeholder}
          className={cn("h-9 text-sm", orgFormErrors[key] && "border-red-400")}
        />
      )}
      {orgFormErrors[key] && (
        <p className="text-xs text-red-500 mt-0.5">{orgFormErrors[key]}</p>
      )}
    </div>
  );

  const orgFormFields = (
    <div className="space-y-3">
      {renderField("Təşkilatın adı", "name", { required: true, placeholder: "məs. Bakı İntellekt Klubu" })}
      {renderField("Qısa adı", "shortName", { placeholder: "məs. BİK" })}
      {renderField("Şəhər", "city", { required: true, placeholder: "məs. Bakı" })}
      <div className="grid grid-cols-2 gap-3">
        {renderField("Telefon", "phone", { placeholder: "+994..." })}
        {renderField("E-poçt", "email", { placeholder: "info@..." })}
      </div>
      {renderField("Haqqında", "information", { textarea: true, placeholder: "Qısa məlumat" })}
      <div className="grid grid-cols-3 gap-3">
        {renderField("Facebook", "facebook", { placeholder: "URL" })}
        {renderField("Instagram", "instagram", { placeholder: "URL" })}
        {renderField("WhatsApp", "whatsapp", { placeholder: "+994..." })}
      </div>
    </div>
  );

  const statusBadge = (role: string, orgName?: string | null) => {
    const isOwner = role === "owner";
    const isIndividual = role === "individual";
    return (
      <div className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold",
        isOwner ? "bg-amber-100 text-amber-700"
          : isIndividual ? "bg-green-100 text-green-700"
          : "bg-blue-100 text-blue-700 dark:text-indigo-300"
      )}>
        {isOwner ? <Crown className="w-3.5 h-3.5" />
          : isIndividual ? <User className="w-3.5 h-3.5" />
          : <Shield className="w-3.5 h-3.5" />}
        {isIndividual ? "Fərdi Təşkilatçı"
          : isOwner ? `Rəhbər${orgName ? ` · ${orgName}` : ""}`
          : `Moderator${orgName ? ` · ${orgName}` : ""}`}
      </div>
    );
  };

  // ── Views ──────────────────────────────────────────────────────────────────

  const renderLoading = () => (
    <div className="flex items-center justify-center py-12">
      <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
    </div>
  );

  const renderNoStatus = () => (
    <div className="space-y-4">
      <div className="rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 p-4 text-sm text-gray-600 dark:text-gray-300">
        Hələ ki, təşkilatçı statusunuz yoxdur. Fərdi təşkilatçı olaraq qeydiyyatdan keçə bilərsiniz.
      </div>
      <Button
        variant="outline"
        className="w-full gap-2 h-10"
        disabled={busy}
        onClick={registerIndividual}
      >
        <User className="w-4 h-4" />
        Fərdi Təşkilatçı Ol
      </Button>
    </div>
  );

  const renderIndividual = () => (
    <div className="space-y-4">
      <div>{statusBadge("individual")}</div>
      <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
        {confirmResign ? (
          <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 p-3 space-y-2">
            <p className="text-xs text-red-700 font-medium">Statusdan çıxmaq istədiyinizə əminsiniz?</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmResign(false)} disabled={busy}>
                Ləğv et
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={resignFromOrganizer} disabled={busy}>
                Bəli, çıx
              </Button>
            </div>
          </div>
        ) : (
          <button
            className="text-xs text-red-400 hover:text-red-600 transition-colors flex items-center gap-1"
            onClick={() => setConfirmResign(true)}
          >
            <LogOut className="w-3.5 h-3.5" />
            Statusdan çıx
          </button>
        )}
      </div>
    </div>
  );

  const renderModerator = () => (
    <div className="space-y-4">
      <div>{statusBadge("moderator", org?.name)}</div>
      {org && (
        <div className="rounded-xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 p-4 space-y-1 text-sm">
          <p className="font-semibold text-gray-800 dark:text-gray-100">{org.name}</p>
          {org.city && <p className="text-gray-500 dark:text-gray-400 text-xs">{org.city}</p>}
          {org.phone && <p className="text-gray-500 dark:text-gray-400 text-xs">{org.phone}</p>}
          {org.email && <p className="text-gray-500 dark:text-gray-400 text-xs">{org.email}</p>}
          {org.information && <p className="text-gray-600 dark:text-gray-300 text-xs mt-1">{org.information}</p>}
          {!org.isApproved && (
            <p className="text-[11px] text-amber-600 mt-1">Təşkilat hələ admin tərəfindən təsdiqlənməyib.</p>
          )}
        </div>
      )}
      <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
        {confirmLeaveOrg ? (
          <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 p-3 space-y-2">
            <p className="text-xs text-red-700 font-medium">Təşkilatdan ayrılmaq istədiyinizə əminsiniz? Fərdi təşkilatçı olaraq qalacaqsınız.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmLeaveOrg(false)} disabled={busy}>
                Ləğv et
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={leaveOrg} disabled={busy}>
                Bəli, ayrıl
              </Button>
            </div>
          </div>
        ) : (
          <button
            className="text-xs text-red-400 hover:text-red-600 transition-colors flex items-center gap-1"
            onClick={() => setConfirmLeaveOrg(true)}
          >
            <LogOut className="w-3.5 h-3.5" />
            Təşkilatdan ayrıl
          </button>
        )}
      </div>
    </div>
  );

  const renderOwner = () => {
    if (!org) return null;
    const otherMembers = org.members.filter(m => m.userId !== currentUserId);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {statusBadge("owner", org.name)}
          {!org.isApproved && (
            <span className="text-[11px] text-amber-600 font-medium">Təsdiq gözlənilir</span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-gray-800">
          {(["info", "members"] as OwnerTab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setOwnerTab(tab)}
              className={cn(
                "pb-2 px-1 mr-4 text-xs font-semibold transition-colors border-b-2 -mb-px",
                ownerTab === tab
                  ? "border-blue-500 text-blue-600 dark:text-indigo-300"
                  : "border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
              )}
            >
              {tab === "info" ? "Məlumatlar" : `Üzvlər (${org.members.length})`}
            </button>
          ))}
        </div>

        {ownerTab === "info" && (
          <div className="space-y-4">
            {orgFormFields}
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={busy}
              onClick={saveOrgDetails}
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
              Yadda Saxla
            </Button>
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              {confirmLeaveOrg ? (
                <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 p-3 space-y-2">
                  <p className="text-xs text-red-700 font-medium">
                    {otherMembers.length > 0
                      ? "Digər üzvlər var. Ayrılmaq üçün əvvəlcə rəhbərliyi başqa üzvə verin."
                      : "Təşkilatdan ayrılmaq istədiyinizə əminsiniz? Fərdi təşkilatçı olaraq qalacaqsınız."}
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setConfirmLeaveOrg(false)} disabled={busy}>
                      Ləğv et
                    </Button>
                    {otherMembers.length === 0 && (
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={leaveOrg} disabled={busy}>
                        Bəli, ayrıl
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  className="text-xs text-red-400 hover:text-red-600 transition-colors flex items-center gap-1"
                  onClick={() => setConfirmLeaveOrg(true)}
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Təşkilatdan ayrıl
                </button>
              )}
            </div>
          </div>
        )}

        {ownerTab === "members" && (
          <div className="space-y-3">
            {/* Member list */}
            <div className="space-y-2">
              {org.members.map(m => {
                const isMe = m.userId === currentUserId;
                return (
                  <div
                    key={m.userId}
                    className="flex items-center justify-between py-2 px-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/50"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {m.name} {m.surname}
                        {isMe && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold dark:text-indigo-300">Siz</span>}
                      </p>
                      <p className={cn(
                        "text-[11px] font-medium",
                        m.role === "owner" ? "text-amber-600" : "text-blue-600 dark:text-indigo-300"
                      )}>
                        {m.role === "owner" ? "Rəhbər" : "Moderator"}
                      </p>
                    </div>
                    {!isMe && (
                      <div className="flex items-center gap-1">
                        <button
                          title="Rəhbərliyi ver"
                          disabled={busy}
                          onClick={() => transferOwnership(m.userId)}
                          className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-700 transition-colors disabled:opacity-50"
                        >
                          <Crown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          title="Üzvlükdən çıxar"
                          disabled={busy}
                          onClick={() => removeMember(m.userId)}
                          className="p-1.5 rounded-lg hover:bg-red-50 dark:bg-red-950 text-red-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add member */}
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Üzv əlavə et</p>
              <div className="relative">
                <Input
                  value={selectedNewMember ? selectedNewMember.label : memberSearch}
                  onChange={e => {
                    if (selectedNewMember) {
                      setSelectedNewMember(null);
                    }
                    searchMembers(e.target.value);
                  }}
                  placeholder="Ad, soyad ilə axtar..."
                  className="h-9 text-sm pr-8"
                />
                {memberSearchLoading && (
                  <RefreshCw className="absolute right-2.5 top-2.5 w-4 h-4 text-gray-300 animate-spin" />
                )}
                {memberDropdown && memberSearchResults.length > 0 && !selectedNewMember && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                    {memberSearchResults.map(u => (
                      <button
                        key={u.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center justify-between"
                        onClick={() => {
                          setSelectedNewMember(u);
                          setMemberDropdown(false);
                          setMemberSearch("");
                        }}
                      >
                        <span>{u.label}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                className="mt-2 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!selectedNewMember || busy}
                onClick={addMember}
              >
                <Plus className="w-3.5 h-3.5" />
                Əlavə et
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────

  const content = () => {
    switch (view) {
      case "loading":       return renderLoading();
      case "no-status":     return renderNoStatus();
      case "individual":    return renderIndividual();
      case "moderator":     return renderModerator();
      case "owner":         return renderOwner();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-500 dark:text-indigo-300" />
            Təşkilatçı Paneli
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-100 px-3 py-2 text-xs text-red-600 font-medium">
            {error}
          </div>
        )}

        {content()}
      </DialogContent>
    </Dialog>
  );
};

export default OrganizerManagementDialog;
